import React, { PropsWithChildren, useEffect, useState } from 'react';
import styled from 'styled-components';

import { ProductUpdate, EntityUpdateRequest } from '../taskAuthorizations';
import { classNames, wfpFormat, withDecimal } from '../../utils/utils';
import { findProductsByGroupId } from '../../api/product-management';
import { loadProduct } from '../../apiClient';

export const rightBorder: React.CSSProperties = {
    borderRight: '1px solid #e8e8e8',
};
export const leftBorder: React.CSSProperties = {
    borderLeft: '1px solid #e8e8e8',
};
export const verticalBorders: React.CSSProperties = {
    borderRight: '1px solid #e8e8e8',
    borderLeft: '1px solid #e8e8e8',
};

interface Props {
    updates: ProductUpdate[];
    updateRequest?: EntityUpdateRequest;
}

export function ProductChangesList({ updates, children, updateRequest }: PropsWithChildren<Props>) {
    const [productNames, setProductNames] = useState<Record<string, string>>({});

    // Fetch product names when component mounts and updateRequest is available
    useEffect(() => {
        if (updateRequest?.entityId) {
            findProductsByGroupId(updateRequest.entityId, new Date())
                .then((response) => {
                    if (response?.data) {
                        const namesMap: Record<string, string> = {};
                        response.data.forEach((product) => {
                            namesMap[product.productId.toString()] = product.productName;
                        });

                        const updateProductIds = updates.map((update) => update.path);
                        const missingProductIds = updateProductIds.filter((id) => !namesMap[id]);

                        // Fetch missing product names individually
                        if (missingProductIds.length > 0) {
                            const fetchMissingProducts = missingProductIds.map(async (productId) => {
                                try {
                                    if (isNaN(Number(productId))) {
                                        return null;
                                    }
                                    const product = await loadProduct(productId);
                                    return { id: productId, name: product.name };
                                } catch (error) {
                                    // Silently handle error - product name will fallback to ID
                                    return null;
                                }
                            });

                            Promise.all(fetchMissingProducts).then((results) => {
                                results.forEach((result) => {
                                    if (result) {
                                        namesMap[result.id] = result.name;
                                    }
                                });
                                setProductNames(namesMap);
                            });
                        } else {
                            setProductNames(namesMap);
                        }
                    }
                })
                .catch(() => {
                    // Silently handle error - product names will fallback to IDs
                });
        }
    }, [updateRequest?.entityId, updates]);

    const products = updates.map(({ path, oldValue, newValue }) => {
        const isAdded = typeof oldValue === 'string';
        const isRemoved = typeof newValue === 'string';
        const productName = productNames[path] || path; // Use product name if available, otherwise fallback to path
        return { path, oldValue, newValue, isAdded, isRemoved, productName };
    });
    const changedProducts = products.filter((product) => !product.isRemoved && !product.isAdded);
    const addedProducts = products.filter((product) => !product.isRemoved && product.isAdded);
    const removedProducts = products.filter((product) => product.isRemoved && !product.isAdded);
    return (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
            <table className="wfp-table--striped mt4">
                <thead>
                    <tr>
                        <th>Product</th>
                        <th colSpan={3} style={verticalBorders}>
                            Old value
                        </th>
                        <th colSpan={3}>New value</th>
                    </tr>
                    <tr>
                        <td />
                        <td style={leftBorder}>Min price</td>
                        <td>Max price</td>
                        <td style={rightBorder}>Fixed price</td>
                        <td>Min price</td>
                        <td>Max price</td>
                        <td>Fixed price</td>
                    </tr>
                </thead>
                <tbody>
                    {[...addedProducts, ...changedProducts, ...removedProducts].map(
                        ({ path, oldValue, newValue, isAdded, isRemoved, productName }) => {
                            const isMinPriceChanged = Boolean(String(newValue?.minPrice).trim().length);
                            const isMaxPriceChanged = Boolean(String(newValue?.maxPrice).trim().length);
                            return (
                                <Row
                                    className={classNames({
                                        'cursor-pointer': true,
                                        added: isAdded,
                                        removed: isRemoved,
                                        changed: !isAdded && !isRemoved,
                                    })}
                                    key={path}
                                >
                                    <td>{productName ? wfpFormat(productName) : '-'}</td>
                                    <td style={leftBorder}>
                                        {isMinPriceChanged ? withDecimal(oldValue?.minPrice) : ''}
                                    </td>
                                    <td>{isMaxPriceChanged ? withDecimal(oldValue?.maxPrice) : ''}</td>
                                    <td style={rightBorder}>{isAdded ? '' : oldValue?.isFixed ? 'Yes' : 'No'}</td>
                                    <td>{isMinPriceChanged ? withDecimal(newValue?.minPrice) : ''}</td>
                                    <td>{isMaxPriceChanged ? withDecimal(newValue?.maxPrice) : ''}</td>
                                    <td>{isRemoved ? '' : newValue?.isFixed ? 'Yes' : 'No'}</td>
                                </Row>
                            );
                        }
                    )}
                    {children || null}
                </tbody>
            </table>
        </div>
    );
}

const Row = styled.tr`
    &.added {
        color: green;
    }

    &.removed {
        color: red;
    }
    &.changed {
        color: #ffc600;
    }
    td {
        color: inherit !important;
    }
`;
